import { useEffect, useMemo, useState } from 'react';
import {
  getInstagramPosts,
  createInstagramPost,
  updateInstagramPost,
  deleteInstagramPost,
  getStudentProfiles,
} from '../../services/api';
import { parseInstagramUrl } from '../../utils/instagramUrl';

// Admin side of Instagram tagging: paste a post link, pick every student who
// appears in it, save. One post is one record however many students it
// features, and a student collects posts over time. The parent portal will
// later show each parent the posts their child is tagged in.

const FIELD =
  'w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm transition-shadow duration-150 outline-none placeholder:text-gray-400 focus:border-purple-400 focus:ring-2 focus:ring-purple-500/15';

const KIND_LABEL = { p: 'Post', reel: 'Reel', tv: 'Video' };

const IgGlyph = ({ className = 'w-4 h-4' }) => (
  <svg aria-hidden="true" className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <rect x="3" y="3" width="18" height="18" rx="5" strokeWidth={2} />
    <circle cx="12" cy="12" r="3.5" strokeWidth={2} />
    <path strokeLinecap="round" strokeWidth={2} d="M16.9 7.1h.01" />
  </svg>
);

const EMBED_SRC = 'https://www.instagram.com/embed.js';

/**
 * Instagram's official embed: a marker blockquote that embed.js swaps for the
 * real post. The script is loaded once; every later mount just asks it to
 * re-scan the page. Requires the post to be public — Amrit's page is.
 */
const InstagramEmbed = ({ url }) => {
  useEffect(() => {
    if (window.instgrm?.Embeds) {
      window.instgrm.Embeds.process();
      return undefined;
    }
    let script = document.querySelector(`script[src="${EMBED_SRC}"]`);
    if (!script) {
      script = document.createElement('script');
      script.src = EMBED_SRC;
      script.async = true;
      document.body.appendChild(script);
    }
    const onLoad = () => window.instgrm?.Embeds?.process();
    script.addEventListener('load', onLoad);
    return () => script.removeEventListener('load', onLoad);
  }, [url]);

  return (
    <blockquote
      className="instagram-media"
      data-instgrm-permalink={url}
      data-instgrm-version="14"
      style={{ margin: '0 auto', maxWidth: '540px', width: '100%' }}
    >
      <a href={url} target="_blank" rel="noreferrer">
        View this post on Instagram
      </a>
    </blockquote>
  );
};

// A selection entry is {id, displayName, keys, classLabels} — usually built
// from a StudentProfile, but a tag whose profile no longer resolves still
// becomes an entry (from the raw key) so editing never silently drops it.
const entryFromProfile = (profile) => ({
  id: profile.id,
  displayName: profile.displayName,
  keys: profile.studentKeys,
  classLabels: (profile.rosterEntries || []).map((e) => e.classLabel),
});

const SelectedChips = ({ selected, onRemove }) =>
  selected.length === 0 ? null : (
    <div className="flex flex-wrap gap-1.5">
      {selected.map((entry) => (
        <span
          key={entry.id}
          className="inline-flex items-center gap-1.5 rounded-full bg-purple-100 py-1 pl-2.5 pr-1.5 text-xs font-medium text-purple-700"
        >
          {entry.displayName}
          <button
            type="button"
            onClick={() => onRemove(entry)}
            className="rounded-full p-0.5 text-purple-400 transition-colors hover:bg-purple-200 hover:text-purple-700"
            aria-label={`Untag ${entry.displayName}`}
          >
            <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </span>
      ))}
    </div>
  );

/**
 * Search-and-toggle student picker. Lists every profile (with class context to
 * tell same-name kids apart) and narrows as the admin types.
 */
const StudentPicker = ({ profiles, selectedIds, onToggle }) => {
  const [search, setSearch] = useState('');

  const matches = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return profiles;
    return profiles.filter((p) => p.displayName.toLowerCase().includes(q));
  }, [profiles, search]);

  return (
    <div>
      <input
        type="text"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className={FIELD}
        placeholder="Search students by name…"
      />
      <div className="mt-2 max-h-56 divide-y divide-gray-100 overflow-y-auto rounded-md border border-gray-200 bg-white">
        {profiles.length === 0 ? (
          <p className="px-3 py-4 text-center text-sm text-gray-400">
            The student list didn’t load — refresh the page and try again
          </p>
        ) : matches.length === 0 ? (
          <p className="px-3 py-4 text-center text-sm text-gray-400">No students match “{search.trim()}”</p>
        ) : (
          matches.map((profile) => {
            const isSelected = selectedIds.has(profile.id);
            return (
              <button
                type="button"
                key={profile.id}
                onClick={() => onToggle(entryFromProfile(profile))}
                className={`flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm transition-colors ${
                  isSelected ? 'bg-purple-50' : 'hover:bg-gray-50'
                }`}
              >
                <span className="min-w-0">
                  <span className={`font-medium ${isSelected ? 'text-purple-800' : 'text-gray-800'}`}>
                    {profile.displayName}
                  </span>
                  {profile.rosterEntries?.length > 0 && (
                    <span className="ml-2 text-xs text-gray-400">
                      {profile.rosterEntries.map((e) => e.classLabel).join(' · ')}
                    </span>
                  )}
                </span>
                {isSelected && (
                  <svg className="h-4 w-4 shrink-0 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </button>
            );
          })
        )}
      </div>
    </div>
  );
};

const InstagramPosts = () => {
  const [posts, setPosts] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  const [showAddForm, setShowAddForm] = useState(false);
  const [addUrl, setAddUrl] = useState('');
  const [addCaption, setAddCaption] = useState('');
  const [addSelected, setAddSelected] = useState([]);

  const [editingPost, setEditingPost] = useState(null);
  const [editSelected, setEditSelected] = useState([]);
  const [editCaption, setEditCaption] = useState('');

  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [previewIds, setPreviewIds] = useState(() => new Set());

  useEffect(() => {
    const load = async () => {
      try {
        const [postsRes, profilesRes] = await Promise.all([getInstagramPosts(), getStudentProfiles()]);
        setPosts(postsRes.data);
        setProfiles(profilesRes.data);
      } catch {
        setError('Could not load Instagram posts — try refreshing the page');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  useEffect(() => {
    if (!notice) return undefined;
    const timer = setTimeout(() => setNotice(null), 5000);
    return () => clearTimeout(timer);
  }, [notice]);

  const taggedStudentCount = useMemo(
    () => new Set(posts.flatMap((p) => p.students.map((s) => s.displayName.toLowerCase()))).size,
    [posts]
  );

  const parsedAddUrl = parseInstagramUrl(addUrl);

  const toggleEntry = (setter) => (entry) =>
    setter((current) =>
      current.some((e) => e.id === entry.id)
        ? current.filter((e) => e.id !== entry.id)
        : [...current, entry]
    );

  const removeEntry = (setter) => (entry) => setter((current) => current.filter((e) => e.id !== entry.id));

  const resetAddForm = () => {
    setAddUrl('');
    setAddCaption('');
    setAddSelected([]);
    setShowAddForm(false);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!parsedAddUrl || addSelected.length === 0) return;
    setSaving(true);
    setError(null);
    try {
      const { data } = await createInstagramPost({
        url: addUrl.trim(),
        caption: addCaption.trim(),
        studentKeys: addSelected.map((entry) => entry.keys[0]),
      });
      setPosts((current) => {
        const rest = current.filter((p) => p.id !== data.post.id);
        return [data.post, ...rest];
      });
      setNotice(
        data.merged
          ? 'That post was already linked — the new students were added to it.'
          : `Linked ${KIND_LABEL[data.post.kind].toLowerCase()} with ${data.post.students.length} student${data.post.students.length === 1 ? '' : 's'} tagged.`
      );
      resetAddForm();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not link the post');
    } finally {
      setSaving(false);
    }
  };

  const startEditing = (post) => {
    setEditingPost(post);
    setEditCaption(post.caption);
    // Map each tag back to its profile; a tag whose profile is gone is kept as
    // a raw-key entry so saving the edit never silently drops it.
    setEditSelected(
      post.students.map((student) => {
        const profile = profiles.find((p) => p.studentKeys.includes(student.studentKey));
        return profile
          ? entryFromProfile(profile)
          : { id: `key:${student.studentKey}`, displayName: student.displayName, keys: [student.studentKey], classLabels: [] };
      })
    );
  };

  const handleEditSave = async () => {
    if (!editingPost || editSelected.length === 0) return;
    setSaving(true);
    setError(null);
    try {
      const { data } = await updateInstagramPost(editingPost.id, {
        studentKeys: editSelected.map((entry) => entry.keys[0]),
        caption: editCaption.trim(),
      });
      setPosts((current) => current.map((p) => (p.id === data.post.id ? data.post : p)));
      setEditingPost(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not update the tags');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    setSaving(true);
    setError(null);
    try {
      await deleteInstagramPost(deleteConfirm.id);
      setPosts((current) => current.filter((p) => p.id !== deleteConfirm.id));
      setDeleteConfirm(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not remove the post');
    } finally {
      setSaving(false);
    }
  };

  const togglePreview = (postId) =>
    setPreviewIds((current) => {
      const next = new Set(current);
      if (next.has(postId)) next.delete(postId);
      else next.add(postId);
      return next;
    });

  const formatDate = (dateString) =>
    new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });

  if (loading) {
    return <p className="py-12 text-center text-gray-500">Loading Instagram posts…</p>;
  }

  return (
    <div className="mx-auto max-w-4xl rounded-lg bg-white p-6 shadow-md">
      <div className="mb-6 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <h2 className="text-2xl font-bold text-gray-900">Instagram Posts</h2>
          {posts.length > 0 && (
            <span className="rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-600">
              {posts.length} {posts.length === 1 ? 'post' : 'posts'} · {taggedStudentCount} students tagged
            </span>
          )}
        </div>
        <button
          onClick={() => (showAddForm ? resetAddForm() : setShowAddForm(true))}
          className="rounded bg-purple-600 px-4 py-2 text-white transition-colors hover:bg-purple-700"
        >
          {showAddForm ? 'Cancel' : '+ Link a Post'}
        </button>
      </div>

      {error && <div className="mb-4 rounded border border-red-200 bg-red-50 p-3 text-red-700">{error}</div>}
      {notice && <div className="mb-4 rounded border border-green-200 bg-green-50 p-3 text-green-700">{notice}</div>}

      {showAddForm && (
        <form
          onSubmit={handleCreate}
          className="mb-6 space-y-4 rounded-lg border border-purple-200 bg-purple-50/40 p-4"
        >
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Post link</label>
            <input
              type="text"
              value={addUrl}
              onChange={(e) => setAddUrl(e.target.value)}
              className={FIELD}
              placeholder="https://www.instagram.com/reel/…"
              autoFocus
              required
            />
            {addUrl.trim() && !parsedAddUrl && (
              <p className="mt-1 text-sm text-red-600">
                That doesn’t look like a link to an Instagram post or reel
              </p>
            )}
            <p className="mt-1 text-xs text-gray-500">
              On Instagram, tap Share → Copy link on the post, then paste it here.
            </p>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">
              Who’s in it?{' '}
              <span className="font-normal text-gray-400">
                {addSelected.length > 0 && `${addSelected.length} tagged`}
              </span>
            </label>
            <div className="space-y-2">
              <SelectedChips selected={addSelected} onRemove={removeEntry(setAddSelected)} />
              <StudentPicker
                profiles={profiles}
                selectedIds={new Set(addSelected.map((entry) => entry.id))}
                onToggle={toggleEntry(setAddSelected)}
              />
            </div>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">
              Note <span className="font-normal text-gray-400">(optional)</span>
            </label>
            <input
              type="text"
              value={addCaption}
              onChange={(e) => setAddCaption(e.target.value)}
              className={FIELD}
              placeholder="e.g. Diwali showcase 2026"
            />
          </div>

          <button
            type="submit"
            disabled={saving || !parsedAddUrl || addSelected.length === 0}
            className="rounded bg-purple-600 px-4 py-2 text-white transition-colors hover:bg-purple-700 disabled:opacity-50"
          >
            {saving ? 'Linking…' : 'Link Post'}
          </button>
        </form>
      )}

      <div className="space-y-3">
        {posts.length === 0 ? (
          <div className="rounded-lg border border-dashed border-gray-300 py-10 text-center">
            <IgGlyph className="mx-auto h-8 w-8 text-gray-300" />
            <p className="mt-3 text-gray-500">No posts linked yet</p>
            <p className="mt-1 text-sm text-gray-400">
              Link a post and tag the students in it — parents will see it on their child’s page.
            </p>
          </div>
        ) : (
          posts.map((post) => {
            const isEditing = editingPost?.id === post.id;
            const showPreview = previewIds.has(post.id);
            return (
              <div key={post.id} className="rounded-lg border border-gray-200 bg-gray-50 p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <a
                      href={post.url}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1.5 text-sm font-semibold text-purple-700 hover:underline"
                    >
                      <IgGlyph />
                      {KIND_LABEL[post.kind] || 'Post'}
                      <svg className="h-3.5 w-3.5 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                        />
                      </svg>
                    </a>
                    {post.caption && <p className="mt-0.5 text-sm text-gray-700">{post.caption}</p>}
                    <p className="mt-0.5 text-xs text-gray-400">
                      Linked {formatDate(post.createdAt)}
                      {post.addedBy && ` by ${post.addedBy}`}
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <button
                      onClick={() => togglePreview(post.id)}
                      className="rounded bg-gray-100 px-3 py-1.5 text-sm text-gray-700 transition-colors hover:bg-gray-200"
                    >
                      {showPreview ? 'Hide Preview' : 'Preview'}
                    </button>
                    <button
                      onClick={() => (isEditing ? setEditingPost(null) : startEditing(post))}
                      className="rounded bg-purple-100 px-3 py-1.5 text-sm text-purple-700 transition-colors hover:bg-purple-200"
                    >
                      {isEditing ? 'Cancel' : 'Edit Tags'}
                    </button>
                    <button
                      onClick={() => setDeleteConfirm(post)}
                      className="rounded bg-red-100 px-3 py-1.5 text-sm text-red-700 transition-colors hover:bg-red-200"
                    >
                      Remove
                    </button>
                  </div>
                </div>

                {isEditing ? (
                  <div className="mt-3 space-y-3 rounded-md border border-purple-200 bg-white p-3">
                    <SelectedChips selected={editSelected} onRemove={removeEntry(setEditSelected)} />
                    <StudentPicker
                      profiles={profiles}
                      selectedIds={new Set(editSelected.map((entry) => entry.id))}
                      onToggle={toggleEntry(setEditSelected)}
                    />
                    <input
                      type="text"
                      value={editCaption}
                      onChange={(e) => setEditCaption(e.target.value)}
                      className={FIELD}
                      placeholder="Note (optional)"
                    />
                    {editSelected.length === 0 && (
                      <p className="text-sm text-red-600">
                        A post needs at least one student — use Remove to unlink it entirely.
                      </p>
                    )}
                    <button
                      onClick={handleEditSave}
                      disabled={saving || editSelected.length === 0}
                      className="rounded bg-purple-600 px-4 py-2 text-sm text-white transition-colors hover:bg-purple-700 disabled:opacity-50"
                    >
                      {saving ? 'Saving…' : 'Save Tags'}
                    </button>
                  </div>
                ) : (
                  <div className="mt-3 flex flex-wrap items-center gap-1.5">
                    <span className="text-xs font-medium text-gray-500">
                      {post.students.length} {post.students.length === 1 ? 'student' : 'students'}:
                    </span>
                    {post.students.map((student) => (
                      <span
                        key={student.studentKey}
                        className="rounded-full bg-white px-2.5 py-1 text-xs font-medium text-gray-700 ring-1 ring-gray-200"
                      >
                        {student.displayName}
                      </span>
                    ))}
                  </div>
                )}

                {showPreview && (
                  <div className="mt-3">
                    <InstagramEmbed url={post.url} />
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="mx-4 w-full max-w-md rounded-lg bg-white p-6">
            <h3 className="mb-4 text-lg font-semibold">Remove this post link?</h3>
            <p className="mb-6 text-gray-700">
              The post stays on Instagram — this only removes it from the portal, so parents of the{' '}
              {deleteConfirm.students.length} tagged{' '}
              {deleteConfirm.students.length === 1 ? 'student' : 'students'} will no longer see it.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="rounded border border-gray-300 px-4 py-2 transition-colors hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={saving}
                className="rounded bg-red-600 px-4 py-2 text-white transition-colors hover:bg-red-700 disabled:opacity-50"
              >
                {saving ? 'Removing…' : 'Remove'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InstagramPosts;
